import React, { useState, useEffect, useRef } from 'react';
import { injectIntl } from "react-intl";
import { Link, useRouteMatch, useHistory } from 'react-router-dom';
import TextField from "@FormComponents/TextField";
import stringSanitizer from "stringSanitizer";
import axios from 'axios';
import { Helmet } from "rnd-helmet";
import searchParamsStringify from "@Utils/searchParamsStringify";
import getDisplayValue from "getDisplayValue";
import formatLocationSearchData from "formatLocationSearchData";

function SearchForm({ intl, searchParams, regionList, specialismOptions, searchSubmitRef }) {
  const route = useRouteMatch();
  const history = useHistory();
  const baseUrl = route.path.replace(':searchParams*', '');
  let filterCount = 0;

  if (Object.keys(searchParams).length) {
    if ((searchParams.location || searchParams.city || searchParams.region) && !searchParams.distance) {
      searchParams.distance = process.env.REACT_APP_DEFAULT_DISTANCE_SEARCH;
    }

    const filters = Object.assign({}, searchParams);
    delete filters.type;
    delete filters.page;
    if (filters.city && filters.region) {
      // Count city and region as one filter.
      delete filters.region;
    }
    delete filters.locationValue;
    delete filters.locationData;
    filterCount = Object.keys(filters).length;
  }

  let initialData = {};
  if (searchParams) {
    initialData = searchParams;
  }

  const [options, setOptions] = useState([]);
  const [state, setState] = useState(initialData);
  const autoSuggestRef = useRef();
  const initialRender = useRef(true);

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 941) {
      let paramsObj = { ...searchParams, ...state };
      if (paramsObj.location) {
        paramsObj = { ...paramsObj, ...formatLocationSearchData(paramsObj.location, regionList) };
      }
      const params = searchParamsStringify(paramsObj);
      history.push(`${baseUrl}${params}`);
    }
  }, [state.distance, state.specialism]);

  useEffect(() => {
    if (autoSuggestRef.current) {
      if (typeof window !== "undefined" && window.innerWidth < 941) {
        autoSuggestRef.current.addEventListener('click', suggestionSelect);
      }
    }
  }, [route.params.searchParams]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    setState(searchParams);
  }, [searchParams])

  const suggestionSelect = (event) => {
    let value = event.target.innerHTML;
    if (event.target.localName === "mark") {
      value = event.target.parentElement.innerHTML;
    }

    value = value.replace(new RegExp("<[^>]*>", 'g'), '');
    const params = searchParamsStringify({ ...searchParams, ...formatLocationSearchData(value, regionList) });
    history.push(`${baseUrl}${params}`);

    if (!state.distance) {
      setState((prev) => {
        return {
          ...prev,
          distance: process.env.REACT_APP_DEFAULT_DISTANCE_SEARCH
        }
      });
    }
  };

  const onLocationFieldClick = e => {
    e.persist();
    if (e.target.value && (!options || !options.length)) {
      // Get suggestions, if no options available in the state.
      getAutocompleteOptions(e);
    }
  };

  const getAutocompleteOptions = e => {
    const callbackAlias = '@Callbacks/getLocationAutocomplete';
    const data = {
      searchText: e.target.value
    };

    axios.post('/api/branches/get-callback', {
      callback: callbackAlias,
      currentRoute: {},
      data: data
    }).then(response => {
      setOptions(response.data);

      // Dispatch input event once again, as the event listeners in orbit
      // were already triggered, but the data attributes were not yet ready,
      // because setOptions is a promise and is called after the input event.
      if (typeof document !== 'undefined') {
        // Dispatch the event only if the event is trusted.
        // isTrusted is true only when the event was triggered by the user.
        // It is false when the event was triggered from a script.
        if (e.isTrusted) {
          let event = new CustomEvent('input', { bubbles: true });
          e.target.dispatchEvent(event);
          document.dispatchEvent(event);
        }
      }
    });
  };

  const locationAutocomplete = (event) => {
    // Keep the event attributes.
    event.persist();
    let name = event.target.name;
    let value = event.target.value;
    getAutocompleteOptions(event);
    setState((prev) => {
      // Clear found location data when getting autocomplete options.
      delete prev.locationValue;
      delete prev.locationData;
      delete prev.locationData;
      delete prev.city;
      delete prev.postcode;
      delete searchParams.locationValue;
      delete searchParams.locationData;
      delete searchParams.city;
      delete searchParams.region;
      delete searchParams.postcode;
      return {
        ...prev,
        [name]: value
      }
    });
  };

  const onChange = (event) => {
    let name = event.target.name;
    let value = event.target.value;
    if (value === "_none") {
      value = null;
    }

    setState((prev) => {
      return {
        ...prev,
        [name]: value
      }
    });
  };

  const onKeyDown = (event) => {
    // Submit search on enter key press.
    if (event.keyCode === 13) {
      onSubmit(event);
    }
  };

  const pushDataLayerSearchEvent = () => {
    let searchKeyword = state.location;
    let specialismValue = state.specialism;

    if (!searchKeyword) {
      searchKeyword = '';
    }
    if (!specialismValue || specialismValue === '_none') {
      specialismValue = '';
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "eventTracker",
      eventCategory: "[JE] Branch search",
      eventAction: searchKeyword,
      eventLabel: specialismValue
    });

    window.dataLayer.push({ event_params: null });
    window.dataLayer.push({
      'event': 'interaction',
      'event_params': {
        'event_name': 'branch_search',
        'search_term': specialismValue,
        'search_location': searchKeyword,
        'search_distance': state?.distance,
      }
    });
  }

  const onSubmit = (event) => {
    event.preventDefault();

    pushDataLayerSearchEvent();

    if (searchParams.page) {
      delete searchParams.page;
    }
    if (state.page) {
      delete state.page;
    }
    let paramsObj = { ...searchParams, ...state };
    if (paramsObj.location) {
      paramsObj = { ...paramsObj, ...formatLocationSearchData(paramsObj.location, regionList) };
    }
    const params = searchParamsStringify(paramsObj);

    if (state.location && !state.distance) {
      setState((prev) => {
        return {
          ...prev,
          distance: process.env.REACT_APP_DEFAULT_DISTANCE_SEARCH
        }
      });
    }

    history.push(`${baseUrl}${params}`);
  };

  const clearFilters = (event) => {
    event.preventDefault();

    setState({});

    let searchParamsCleared = {
      location: null,
      distance: null,
      city: null,
      region: null,
      postcode: null,
      specialism: null,
    };

    const params = searchParamsStringify({ ...searchParams, ...searchParamsCleared });
    history.push(`${baseUrl}${params}`);
  }

  let specialismFilterOptions = [
    <option key="none" value="_none">{intl.formatMessage({ id: "SearchForm.SpecialismPlaceholder" })}</option>
  ];
  for (let tid in specialismOptions) {
    let specialismData = specialismOptions[tid];
    specialismFilterOptions.push(
      <option key={specialismData.tid} value={`${stringSanitizer(specialismData.name)}`}>{specialismData.name}</option>
    );
  }

  return (
    <>
      <Helmet>
        <script type="text/javascript" src="/branches/assets/js/auto-suggest.js" />
      </Helmet>
      <div className="filter__clear">
        {
          filterCount ?
          <span className="link" onClick={clearFilters}>
            {intl.formatMessage({ id: "SearchForm.ClearSearch" }, { filterCount: filterCount })}
          </span> : null
        }
      </div>
      <form name="branches-search-form" aria-label="branches-search-form" className="form-group form-group--selection-control">
        <div className="form-group__input" data-rs-auto-suggest={true}>
          <TextField name="location" onChange={locationAutocomplete}
            onClick={onLocationFieldClick}
            onKeyDown={onKeyDown}
            value={getDisplayValue({ ...searchParams, ...state })}
            placeholder={intl.formatMessage({ id: "SearchForm.LocationPlaceholder" })} data-rs-auto-suggest-input={true}
            data-rs-auto-suggest-no-result-message={intl.formatMessage({ id: "Autosuggest.Noresults.Message" })}
            autoComplete="off" />
          <ul className="select-menu__list" id="geo-search-as" data-rs-auto-suggest-list="" data-rs-auto-suggest-values={options.slice(0, 10).join(';')} data-rs-auto-suggest-separator=";" ref={autoSuggestRef} data-rs-use-exact-values={true} />
        </div>
        <div className="form-group__input">
          <select className={`${state.distance ? "" : "untouched"}`} aria-label={intl.formatMessage({ id: "SearchForm.DistancePlaceholder" })} value={state.distance ? state.distance : "_none"} name="distance" onChange={onChange}>
            <option value="_none">{intl.formatMessage({ id: "SearchForm.DistancePlaceholder" })}</option>
            <option value="5">{intl.formatMessage({ id: "SearchForm.SearchDistance5" })}</option>
            <option value="10">{intl.formatMessage({ id: "SearchForm.SearchDistance10" })}</option>
            <option value="25">{intl.formatMessage({ id: "SearchForm.SearchDistance25" })}</option>
            <option value="50">{intl.formatMessage({ id: "SearchForm.SearchDistance50" })}</option>
          </select>
          <span className="select-arrow icon">
            <svg>
              <use xlinkHref="/themes/custom/bluex/dist/assets/image/icons.svg#chevron-down" />
            </svg>
          </span>
        </div>
        <div className="form-group__input">
          <Link to="#" className="button button--m button--full-width" id="sidebar-submit" onClick={onSubmit} ref={searchSubmitRef} aria-label={intl.formatMessage({ id: "SearchForm.SearchSubmit.AccessibilityAriaLabel" })}>
            {intl.formatMessage({ id: "SearchForm.SearchSubmit" })}
          </Link>
        </div>
      </form>
    </>
  );
}

export default injectIntl(SearchForm);
